'use client';

/**
 * /email — Beast Mailer cockpit (ported from VCTronics outreach, engine at :3700).
 * Compose + contacts + sender identities + stats. The mailer service is the engine;
 * this page is the cockpit (same pattern as /crm ↔ sites-hub).
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Mail, Send, Users, Loader2, Plus, RefreshCw, AtSign, MousePointerClick,
  Eye, AlertTriangle, Clock,
} from 'lucide-react';

interface Contact {
  id: string; project: string; email: string; name: string | null;
  company: string | null; status: string; last_contacted_at: string | null;
}
interface Project { name: string; from_addr: string; reply_to: string | null; base_url: string | null }
interface InboxMsg {
  id: string; account: string; project: string | null; from_addr: string | null; from_name: string | null;
  to_addr: string | null; subject: string; snippet: string; body_text: string; date: string; unread: number; folder: string;
}
const FOLDERS = [
  ['primary', '✉️ Primary'], ['updates', '🔔 Updates'], ['social', '👥 Social'],
  ['promotions', '🏷️ Promos'], ['forums', '💬 Forums'], ['spam', '🗑️ Spam'],
] as const;
interface ImapAccount { id: string; name: string; host: string; user: string; last_uid: number; active: number }
interface ProjCount { project: string; n: number; unread: number }

/** stable color per business, VCTronics-style badges */
const BADGE_COLORS = [
  'bg-blue-500/15 text-blue-300', 'bg-purple-500/15 text-purple-300', 'bg-green-500/15 text-green-300',
  'bg-amber-500/15 text-amber-300', 'bg-pink-500/15 text-pink-300', 'bg-cyan-500/15 text-cyan-300',
  'bg-red-500/15 text-red-300', 'bg-lime-500/15 text-lime-300', 'bg-orange-500/15 text-orange-300',
  'bg-teal-500/15 text-teal-300', 'bg-indigo-500/15 text-indigo-300', 'bg-fuchsia-500/15 text-fuchsia-300',
];
const badgeFor = (p: string) => {
  let h = 0; for (let i = 0; i < p.length; i++) h = (h * 31 + p.charCodeAt(i)) >>> 0;
  return BADGE_COLORS[h % BADGE_COLORS.length];
};
const bizName = (p: string) => p.replace(/-ca$/, '').replace(/-/g, ' ');
interface Stats { contacts: number; sent: number; opened: number; clicked: number; failed: number; scheduled: number }

const STATUS_COLORS: Record<string, string> = {
  not_contacted: 'bg-gray-500/15 text-gray-400',
  emailed: 'bg-blue-500/15 text-blue-300',
  opened: 'bg-amber-500/15 text-amber-300',
  clicked: 'bg-purple-500/15 text-purple-300',
  replied: 'bg-green-500/15 text-green-300',
  bounced: 'bg-red-500/15 text-red-300',
  unsubscribed: 'bg-red-500/15 text-red-400',
};

const NON_FACTORY_KEYS = ['tls', 'olga', 'anna', 'astroforyou', 'vctronics', 'aviationprivee', 'charters'];

export default function EmailPage({ scope, scopeName }: { scope?: string[] | 'factory'; scopeName?: string } = {}) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [tab, setTab] = useState<'inbox' | 'compose' | 'contacts' | 'projects'>('inbox');
  const [messages, setMessages] = useState<InboxMsg[]>([]);
  const [accounts, setAccounts] = useState<ImapAccount[]>([]);
  const [openMsg, setOpenMsg] = useState<InboxMsg | null>(null);
  const [polling, setPolling] = useState(false);
  const [aForm, setAForm] = useState({ name: '', user: '', pass: '', host: 'imap.gmail.com' });
  const [folder, setFolder] = useState<string>('primary');
  const [attachments, setAttachments] = useState<{ filename: string; content: string }[]>([]);
  // scope = HARD project scoping (inside /p/[slug]/email): ONLY this project's
  // mail, no other businesses visible anywhere (Boss 2026-07-07).
  const scoped = scope === 'factory' || (Array.isArray(scope) && scope.length > 0);
  const [projectFilter, setProjectFilter] = useState(() => {
    if (Array.isArray(scope) && scope.length) return scope.join(',');
    if (scope === 'factory') return '__pending__'; // resolved once projCounts load
    if (typeof window !== 'undefined') return new URLSearchParams(window.location.search).get('project') || '';
    return '';
  });
  const [projCounts, setProjCounts] = useState<ProjCount[]>([]);
  const [engineDown, setEngineDown] = useState(false);

  // compose
  const [form, setForm] = useState({ project: '', to_email: '', to_name: '', subject: '', body: '' });
  const [sending, setSending] = useState(false);

  // new project
  const [pForm, setPForm] = useState({ name: '', from_addr: '', reply_to: '', base_url: '' });

  const load = useCallback(async () => {
    try {
      const [s, p, c, m, a, pcRes] = await Promise.all([
        fetch(`/api/email/stats${projectFilter ? `?project=${projectFilter}` : ''}`).then(r => r.json()),
        fetch('/api/email/projects').then(r => r.json()),
        fetch(`/api/email/contacts${projectFilter ? `?project=${projectFilter}` : ''}`).then(r => r.json()),
        fetch(`/api/email/inbox?folder=${folder}${projectFilter ? `&project=${projectFilter}` : ''}`).then(r => r.json()),
        fetch('/api/email/accounts').then(r => r.json()),
        fetch('/api/email/inbox/projects').then(r => r.json()),
      ]);
      if (s.error || p.error) { setEngineDown(true); return; }
      setEngineDown(false);
      setStats(s);
      setProjects(Array.isArray(p) ? p : []);
      setContacts(Array.isArray(c) ? c : []);
      setMessages(Array.isArray(m) ? m : []);
      setAccounts(Array.isArray(a) ? a : []);
      const pc: ProjCount[] = Array.isArray(pcRes) ? pcRes : [];
      setProjCounts(pc);
      // factory scope: all business keys EXCEPT the non-factory projects
      if (scope === 'factory') {
        const keys = pc.map(p => p.project).filter(k => k && !NON_FACTORY_KEYS.includes(k));
        setProjectFilter(keys.length ? keys.join(',') : '__none__');
      }
    } catch { setEngineDown(true); }
  }, [projectFilter]);

  useEffect(() => { load(); }, [load, folder]);

  // Always fresh — refetch every 60s + on window focus (engine polls IMAP/hub itself).
  useEffect(() => {
    const iv = setInterval(load, 60_000);
    window.addEventListener('focus', load);
    return () => { clearInterval(iv); window.removeEventListener('focus', load); };
  }, [load]);

  const sendEmail = async () => {
    if (!form.to_email || !form.subject || !form.body) { toast.error('To, subject and body are required'); return; }
    setSending(true);
    try {
      const res = await fetch('/api/email/send', {
        method: 'POST',
        body: JSON.stringify({ ...form, project: form.project || 'default', to_name: form.to_name || undefined, attachments: attachments.length ? attachments : undefined }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Sent ✓ (resend ${data.resend_id?.slice(0, 8)}…)`);
        setForm(f => ({ ...f, to_email: '', to_name: '', subject: '', body: '' }));
        setAttachments([]);
        load();
      } else toast.error(data.error || 'Send failed');
    } catch (e: any) { toast.error(e.message); }
    setSending(false);
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const r = await fetch('/api/email/inbox/poll', { method: 'POST' }).then(r => r.json());
      toast.success(`Polled: ${JSON.stringify(r)}`);
      load();
    } catch (e: any) { toast.error(e.message); }
    setPolling(false);
  };

  const addAccount = async () => {
    if (!aForm.name || !aForm.user || !aForm.pass) { toast.error('Name, user and app password required'); return; }
    const r = await fetch('/api/email/accounts', { method: 'POST', body: JSON.stringify(aForm) }).then(r => r.json());
    if (r.success) { toast.success('IMAP account added'); setAForm({ name: '', user: '', pass: '', host: 'imap.gmail.com' }); load(); }
    else toast.error(r.error || 'Failed');
  };

  const openMessage = (m: InboxMsg) => {
    setOpenMsg(m);
    if (m.unread) { fetch('/api/email/inbox/read', { method: 'POST', body: JSON.stringify({ id: m.id }) }); m.unread = 0; }
  };

  const addProject = async () => {
    if (!pForm.name || !pForm.from_addr) { toast.error('Name and from address required'); return; }
    const res = await fetch('/api/email/projects', { method: 'POST', body: JSON.stringify(pForm) });
    const data = await res.json();
    if (data.success) { toast.success(`Sender "${pForm.name}" saved`); setPForm({ name: '', from_addr: '', reply_to: '', base_url: '' }); load(); }
    else toast.error(data.error || 'Failed');
  };

  const statCards = stats ? [
    { label: 'Contacts', value: stats.contacts, icon: Users },
    { label: 'Sent', value: stats.sent, icon: Send },
    { label: 'Opened', value: stats.opened, icon: Eye },
    { label: 'Clicked', value: stats.clicked, icon: MousePointerClick },
    { label: 'Scheduled', value: stats.scheduled, icon: Clock },
    { label: 'Failed', value: stats.failed, icon: AlertTriangle },
  ] : [];

  const inputCls = 'w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-400/50';

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header — hidden when scoped inside a project (project layout owns the chrome) */}
      {!scoped && (
      <header className="border-b border-gray-800 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-blue-400" />
              <h1 className="text-xl font-semibold text-white">BEAST Email — All Businesses</h1>
            </div>
            <div className="flex items-center gap-2">
              <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-300">
                <option value="">All businesses</option>
                {projCounts.map(p => <option key={p.project} value={p.project}>{bizName(p.project)} ({p.n})</option>)}
                {projects.filter(p => !projCounts.some(x => x.project === p.name)).map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
              <button onClick={load} className="p-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-white">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>
      )}

      {/* Tabs — VCTronics underline style */}
      <div className="border-b border-gray-800 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {([['inbox', `Inbox (${messages.filter(m => m.unread).length})`], ['compose', 'Compose'], ['contacts', `Contacts (${contacts.length})`], ['projects', `Senders (${projects.length})`]] as const).map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${tab === id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-700'}`}>
                {label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

      {engineDown && (
        <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-200">
          beast-mailer engine is down. Start it: <code className="text-red-100">pm2 start ~/beast/tools/mailer/server.js --name beast-mailer</code>
        </div>
      )}

      {/* stats */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map(({ label, value, icon: Icon }) => (
          <div key={label} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1"><Icon className="w-3.5 h-3.5" />{label}</div>
            <div className="text-xl font-semibold text-white">{value}</div>
          </div>
        ))}
      </div>


      {tab === 'inbox' && (
        <div className="space-y-4">
          {accounts.length === 0 && (
            <div className="rounded-xl border border-dashed border-white/[0.15] p-4 space-y-3">
              <div className="text-sm text-gray-300">No IMAP accounts yet. Add the Gmail/Workspace inbox where your business mail lands (use a Google <b>App Password</b>, not the real password: myaccount.google.com/apppasswords).</div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Label (e.g. main-gmail)" value={aForm.name} onChange={e => setAForm(f => ({ ...f, name: e.target.value }))} className={inputCls} />
                <input placeholder="IMAP host" value={aForm.host} onChange={e => setAForm(f => ({ ...f, host: e.target.value }))} className={inputCls} />
                <input placeholder="Email (user)" value={aForm.user} onChange={e => setAForm(f => ({ ...f, user: e.target.value }))} className={inputCls} />
                <input placeholder="App password" type="password" value={aForm.pass} onChange={e => setAForm(f => ({ ...f, pass: e.target.value }))} className={inputCls} />
              </div>
              <button onClick={addAccount} className="px-4 py-2 rounded-lg bg-blue-500/20 border border-blue-400/40 text-sm text-blue-200">Connect inbox</button>
            </div>
          )}
          {accounts.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {FOLDERS.map(([id, label]) => (
                <button key={id} onClick={() => setFolder(id)}
                  className={`px-3 py-1.5 rounded-lg text-xs border ${folder === id ? 'bg-blue-500/20 border-blue-400/40 text-blue-200' : 'bg-white/[0.03] border-white/10 text-gray-400 hover:text-white'}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {accounts.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-400">Accounts: {accounts.map(a => `${a.name} (${a.user})`).join(', ')} · auto-poll every 60s</div>
              <button onClick={pollNow} disabled={polling} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-white/10 text-xs text-gray-300 hover:text-white">
                {polling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Poll now
              </button>
            </div>
          )}
          {/* business chips — hidden when scoped to one project */}
          {!scoped && (
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setProjectFilter('')}
              className={`px-2.5 py-1 rounded-full text-xs border ${!projectFilter ? 'bg-blue-500/20 border-blue-400/40 text-blue-200' : 'bg-gray-900 border-gray-800 text-gray-400 hover:text-white'}`}>
              All ({projCounts.reduce((s, p) => s + p.n, 0)})
            </button>
            {projCounts.map(p => (
              <button key={p.project} onClick={() => setProjectFilter(projectFilter === p.project ? '' : p.project)}
                className={`px-2.5 py-1 rounded-full text-xs border ${projectFilter === p.project ? 'bg-blue-500/20 border-blue-400/40 text-blue-200' : `border-gray-800 hover:border-gray-600 ${badgeFor(p.project)}`}`}>
                {bizName(p.project)} ({p.n}){p.unread ? ` · ${p.unread} new` : ''}
              </button>
            ))}
          </div>
          )}
          <div className="rounded-xl border border-gray-800 bg-gray-900/40 overflow-hidden">
            {messages.map(m => (
              <button key={m.id} onClick={() => openMessage(m)}
                className={`w-full text-left px-4 py-3 border-b border-gray-800/60 hover:bg-gray-800/40 flex items-start gap-3 ${m.unread ? 'bg-gray-800/30' : ''}`}>
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${m.unread ? 'bg-blue-400' : 'bg-transparent'}`} />
                <div className="w-36 flex-shrink-0 pt-0.5">
                  {m.project
                    ? <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${badgeFor(m.project)}`}>{bizName(m.project)}</span>
                    : <span className="inline-block px-2 py-0.5 rounded text-[11px] bg-gray-800 text-gray-500">personal</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm truncate ${m.unread ? 'text-white font-medium' : 'text-gray-300'}`}>{m.from_name || m.from_addr}</span>
                    {m.from_name && m.from_addr && <span className="text-xs text-gray-500 truncate hidden sm:inline">&lt;{m.from_addr}&gt;</span>}
                    <span className="ml-auto text-xs text-gray-500 flex-shrink-0">{m.date?.slice(0, 16).replace('T', ' ')}</span>
                  </div>
                  <div className={`text-sm truncate ${m.unread ? 'text-gray-200' : 'text-gray-400'}`}>{m.subject}</div>
                  <div className="text-xs text-gray-500 truncate">to {m.to_addr || '—'} — {m.snippet}</div>
                </div>
              </button>
            ))}
            {messages.length === 0 && <div className="px-4 py-10 text-center text-sm text-gray-500">{scoped ? `No ${scopeName || 'project'} mail yet — this project's mailbox isn't connected to Beast` : accounts.length ? 'No mail in this folder' : 'Connect an inbox above to start reading mail here'}</div>}
          </div>
          {openMsg && (
            <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={() => setOpenMsg(null)}>
              <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-1">
                  {openMsg.project && <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${badgeFor(openMsg.project)}`}>{bizName(openMsg.project)}</span>}
                  <div className="text-white font-medium">{openMsg.subject}</div>
                </div>
                <div className="text-xs text-gray-400 mb-1">From: {openMsg.from_name} &lt;{openMsg.from_addr}&gt;</div>
                <div className="text-xs text-gray-500 mb-4">To: {openMsg.to_addr} · {openMsg.date?.slice(0, 19).replace('T', ' ')}</div>
                <pre className="text-sm text-gray-200 whitespace-pre-wrap font-sans">{openMsg.body_text}</pre>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => { setForm(f => ({ ...f, to_email: openMsg.from_addr || '', to_name: openMsg.from_name || '', subject: `Re: ${openMsg.subject}`, body: `\n\n---\n${openMsg.body_text?.split('\n').slice(0, 10).map(l => `> ${l}`).join('\n')}`, project: openMsg.project || '' })); setOpenMsg(null); setTab('compose'); }}
                    className="px-4 py-2 rounded-lg bg-blue-500/20 border border-blue-400/40 text-sm text-blue-200">Reply</button>
                  <button onClick={() => { setForm(f => ({ ...f, to_email: openMsg.from_addr || '', to_name: openMsg.from_name || '', subject: `Follow-up: ${openMsg.subject.replace(/^(Re|Fwd|Follow-up): */i, '')}`, project: openMsg.project || '' })); setOpenMsg(null); setTab('compose'); }}
                    className="px-4 py-2 rounded-lg bg-purple-500/20 border border-purple-400/40 text-sm text-purple-200">Follow-up</button>
                  <button onClick={() => setOpenMsg(null)} className="px-4 py-2 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-gray-300">Close</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'compose' && (
        <div className="max-w-2xl space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select value={form.project} onChange={e => setForm(f => ({ ...f, project: e.target.value }))} className={inputCls}>
              <option value="">Sender: default (DEFAULT_FROM)</option>
              {projects.map(p => <option key={p.name} value={p.name}>{p.name} — {p.from_addr}</option>)}
            </select>
            <input placeholder="Recipient name (optional)" value={form.to_name} onChange={e => setForm(f => ({ ...f, to_name: e.target.value }))} className={inputCls} />
          </div>
          <input placeholder="To email *" value={form.to_email} onChange={e => setForm(f => ({ ...f, to_email: e.target.value }))} className={inputCls} />
          <input placeholder="Subject *  ({{first_name}}, {{company}} supported)" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className={inputCls} />
          <textarea placeholder="Body *  — plain text; links auto-tracked; {{first_name}} etc. substituted" rows={10} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} className={inputCls} />
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer w-fit px-3 py-2 rounded-lg bg-white/[0.03] border border-dashed border-white/[0.15] hover:text-white">
              📎 Attach files
              <input type="file" multiple className="hidden" onChange={async e => {
                const files = Array.from(e.target.files || []);
                const read = await Promise.all(files.map(f => new Promise<{ filename: string; content: string }>((res) => {
                  const r = new FileReader();
                  r.onload = () => res({ filename: f.name, content: String(r.result).split(',')[1] });
                  r.readAsDataURL(f);
                })));
                setAttachments(a => [...a, ...read]);
                e.target.value = '';
              }} />
            </label>
            {attachments.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-gray-300">
                <span className="px-2 py-1 rounded bg-white/[0.05] border border-white/10">📎 {a.filename} ({Math.round(a.content.length * 0.75 / 1024)} KB)</span>
                <button onClick={() => setAttachments(list => list.filter((_, j) => j !== i))} className="text-gray-500 hover:text-red-400">✕</button>
              </div>
            ))}
          </div>
          <button onClick={sendEmail} disabled={sending}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-500/20 border border-blue-400/40 text-blue-200 hover:bg-blue-500/30 disabled:opacity-50">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
          </button>
        </div>
      )}

      {tab === 'contacts' && (
        <div className="rounded-xl border border-white/[0.08] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-gray-400 text-xs">
              <tr>{['Email', 'Name', 'Company', 'Project', 'Status', 'Last contact'].map(h => <th key={h} className="text-left px-4 py-2 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.id} className="border-t border-white/[0.05] text-gray-300">
                  <td className="px-4 py-2 text-white">{c.email}</td>
                  <td className="px-4 py-2">{c.name || '—'}</td>
                  <td className="px-4 py-2">{c.company || '—'}</td>
                  <td className="px-4 py-2 text-gray-400">{c.project}</td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[c.status] || STATUS_COLORS.not_contacted}`}>{c.status}</span></td>
                  <td className="px-4 py-2 text-gray-500 text-xs">{c.last_contacted_at?.slice(0, 16).replace('T', ' ') || '—'}</td>
                </tr>
              ))}
              {contacts.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No contacts yet — send an email or import via CLI</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'projects' && (
        <div className="space-y-4 max-w-2xl">
          {projects.map(p => (
            <div key={p.name} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 flex items-start gap-3">
              <AtSign className="w-4 h-4 text-blue-300 mt-0.5" />
              <div className="text-sm">
                <div className="text-white font-medium">{p.name}</div>
                <div className="text-gray-400">{p.from_addr}</div>
                {p.reply_to && <div className="text-gray-500 text-xs">reply-to: {p.reply_to}</div>}
                {p.base_url && <div className="text-gray-500 text-xs">tracking: {p.base_url}</div>}
              </div>
            </div>
          ))}
          <div className="rounded-xl border border-dashed border-white/[0.15] p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-300"><Plus className="w-4 h-4" /> New sender identity</div>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Project name (e.g. olga)" value={pForm.name} onChange={e => setPForm(f => ({ ...f, name: e.target.value }))} className={inputCls} />
              <input placeholder='From: Name <email@domain>' value={pForm.from_addr} onChange={e => setPForm(f => ({ ...f, from_addr: e.target.value }))} className={inputCls} />
              <input placeholder="Reply-to (optional)" value={pForm.reply_to} onChange={e => setPForm(f => ({ ...f, reply_to: e.target.value }))} className={inputCls} />
              <input placeholder="Tracking base URL (optional)" value={pForm.base_url} onChange={e => setPForm(f => ({ ...f, base_url: e.target.value }))} className={inputCls} />
            </div>
            <button onClick={addProject} className="px-4 py-2 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-gray-200 hover:bg-white/[0.1]">Save sender</button>
          </div>
        </div>
      )}
      </main>
    </div>
  );
}
